import { z } from 'zod'
import { Result } from '@cplug/frontend-core/utils'
import {
  factoryListDTO,
  factoryListDTOWithOptionalMeta,
  type DTODataList,
  type DTODataListWithOptionalMeta
} from '@cplug/frontend-core/gateways'
import type { PageList } from '@cplug/frontend-core/models'
import type { AppCatalogProductCategory } from '../models/AppCatalogProductCategory'
import {
  AppCatalogProductCategoryDTO,
  mapAppCatalogProductCategoryToDTO,
  mapDTOToAppCatalogProductCategory
} from './DTOs/AppCatalogProductCategoryDTO'
import { CPBaseListPaginatedGateway } from '~/shared/gateways/CPBaseListPaginatedGateway'

const ListDTOSchema = z.object({ categories: AppCatalogProductCategoryDTO.array() })
type ListDTO = z.infer<typeof ListDTOSchema>

export default class CPAppCatalogProductCategoryGateway extends CPBaseListPaginatedGateway<
  AppCatalogProductCategory,
  AppCatalogProductCategoryDTO,
  ListDTO
> {
  protected override listField = 'categories' as const
  protected override dtoToModelMapper = mapDTOToAppCatalogProductCategory

  async listCatalogProductCategoriesByApp (
    appId: number,
    page: number = 1,
    limit: number = 20,
    options = {}
  ): Promise<Result<PageList<AppCatalogProductCategory>>> {
    const response = await this.get(`apps/${appId}/categories`, { page, per_page: limit, ...options })

    return this.parseResponse(response, this.listSchema, this.listDtoToModelPageListMapper.bind(this))
  }

  async updateCatalogProductCategories (
    appId: number,
    catalogCategories: AppCatalogProductCategory[]
  ): Promise<Result<PageList<AppCatalogProductCategory>>> {
    const response = await this.put(`apps/${appId}/categories`, {
      categories: catalogCategories.map(it => mapAppCatalogProductCategoryToDTO(it))
    })
    return this.parseResponse(response, this.listSchemaWithoutMeta, this.listDtoToModelPageListMapper.bind(this))
  }

  private get listSchemaWithoutMeta (): z.ZodType<DTODataListWithOptionalMeta<ListDTO>> {
    return factoryListDTOWithOptionalMeta(ListDTOSchema) as unknown as z.ZodType<DTODataListWithOptionalMeta<ListDTO>>
  }

  get listSchema (): z.ZodType<DTODataList<ListDTO>> {
    return factoryListDTO(ListDTOSchema) as unknown as z.ZodType<DTODataList<ListDTO>>
  }
}
